import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import Paper from "@mui/material/Paper";
import ProjectsListCellView from "src/pages/ProjectsPage/ProjectsListCellView";
import { ts } from "src/utils/translate";
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { ProjectsSortBy, ProjectsSortOrder, projectsUiSlice } from "src/api/projects-ui-slice";
import { GetProjectResponseProject } from "src/model/api/v2/data-contracts";
import { theme } from "src/theme/mui-theme";

type Props = {
    projects: GetProjectResponseProject[]
};

export function ProjectsListView({ projects }: Props) {

    const sortBy = useAppSelector(state => state.projectsUi.sortBy);
    const sortOrder = useAppSelector(state => state.projectsUi.sortOrder);
    const dispatch = useAppDispatch();

    const handleSortChange = (column: ProjectsSortBy) => {
        const isSameColumn = sortBy === column;
        const newOrder: ProjectsSortOrder = isSameColumn && sortOrder === 'asc' ? 'desc' : 'asc';

        dispatch(projectsUiSlice.actions.setSortBy({ sortBy: column, sortOrder: newOrder }));
    };

    return (
        <TableContainer component={Paper} elevation={0} sx={{ height: '100%', overflow: 'auto' }}>
            <Table stickyHeader sx={{
                borderCollapse: 'collapse',
                '& td, & th': {
                    borderBottom: '1px solid #e0e0e0',
                    borderRight: 'none',
                    borderLeft: 'none',
                    borderTop: 'none',
                },
                '& th': {
                    fontWeight: 400,
                    backgroundColor: theme.palette.background.paper,
                },
                boxShadow: 'none',
                backgroundColor: 'transparent'
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={sortBy === 'projectName' ? sortOrder : undefined}>
                            <TableSortLabel
                                active={sortBy === 'projectName'}
                                direction={sortBy === 'projectName' ? sortOrder : undefined}
                                onClick={() => handleSortChange('projectName')}
                            >
                                {ts('project_name_label', 'Project name')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'address' ? sortOrder : undefined}>
                            <TableSortLabel
                                active={sortBy === 'address'}
                                direction={sortBy === 'address' ? sortOrder : undefined}
                                onClick={() => handleSortChange('address')}
                            >
                                {ts('project_address_label', 'Address')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={ false }>
                            {ts('project_tag_label', 'Tags')}
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'createdAt' ? sortOrder : undefined}>
                            <TableSortLabel
                                active={sortBy === 'createdAt'}
                                direction={sortBy === 'createdAt' ? sortOrder : undefined}
                                onClick={() => handleSortChange('createdAt')}
                            >
                                {ts('project_created_time_label', 'Created time')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={sortBy === 'lastModified' ? sortOrder : undefined}>
                            <TableSortLabel
                                active={sortBy === 'lastModified'}
                                direction={sortBy === 'lastModified' ? sortOrder : undefined}
                                onClick={() => handleSortChange('lastModified')}
                            >
                                {ts('project_last_modified_label', 'Last modified')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {projects.length === 0 ? (
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': {
                                    borderBottom: 'none',
                                    paddingTop: 28,
                                },
                            }}
                        >
                            <TableCell colSpan={6} align="center">
                                {ts('no_results_found', 'No results found.')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        projects.map((p) => (
                            <ProjectsListCellView
                                key={p.project.id}
                                extendedProject={p}
                            />
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
